import {
  Button,
  Checkbox,
  ColorInput,
  Divider,
  Flex,
  Group,
  InputLabel,
  NumberInput,
  TextInput,
  Title,
} from "@mantine/core";
import React, { Dispatch, SetStateAction, useEffect, useState } from "react";
import {
  FanZoneData,
  RectangleProperties,
  TextProperties,
} from "../../../../../../api/scheme";
import { defaultZone, defaultZoneTranslations } from "../../constants";

type Lang = "en" | "ro" | "ru";
type FanZoneProps = {
  px?: string;
  selectedKeyFanZone: string;
  undoStack: any[];
  setUndoStack: Dispatch<SetStateAction<any[]>>;
  data: any;
  setData: Dispatch<SetStateAction<any>>;
  t: (key: string) => string;
};

export const FanZone = ({
  px,
  setData,
  setUndoStack,
  undoStack,
  data,
  selectedKeyFanZone,
  t,
}: FanZoneProps) => {
  const [newZone, setNewZone] = useState<FanZoneData>(defaultZone);
  const isEditMode = selectedKeyFanZone?.startsWith("fz-");

  const zoneIndex = isEditMode
    ? Number(selectedKeyFanZone.replace("fz-", ""))
    : -1;

  // Sync fan-zone data when in edit mode
  useEffect(() => {
    const hallParts = data?.hall_parts_data ?? [];
    if (isEditMode && !isNaN(zoneIndex) && hallParts[zoneIndex]) {
      setNewZone(hallParts[zoneIndex]);
    } else {
      setNewZone(defaultZone);
    }
  }, [selectedKeyFanZone, data]);

  const updateZone = (path: string[], value: any) => {
    setNewZone((prev) => {
      const updated = { ...prev };
      let obj: any = updated;

      for (let i = 0; i < path.length - 1; i++) {
        if (typeof obj[path[i]] !== "object" || obj[path[i]] === null) {
          obj[path[i]] = {};
        } else {
          obj[path[i]] = { ...obj[path[i]] };
        }
        obj = obj[path[i]];
      }

      obj[path[path.length - 1]] = value;
      return updated;
    });
  };

  const handleSave = () => {
    setUndoStack([...undoStack, data]);

    const hallParts = data?.hall_parts_data ?? [];

    if (isEditMode && !isNaN(zoneIndex) && hallParts[zoneIndex]) {
      const updatedZones = [...hallParts];

      // Force name as "fan_zone_<index>"
      updatedZones[zoneIndex] = { ...newZone, name: `fan_zone_${zoneIndex}` };

      setData((prev: any) => ({
        ...prev,
        hall_parts_data: updatedZones,
      }));
    } else {
      const newIndex = hallParts.length;

      // Force name as "fan_zone_<new index>"
      const newZoneWithName = { ...newZone, name: `fan_zone_${newIndex}` };
      const updatedZones = [...hallParts, newZoneWithName];

      setData((prev: any) => ({
        ...prev,
        hall_parts_data: updatedZones,
      }));
    }
  };

  const stylesRectangles: Partial<RectangleProperties["styles"]> =
    newZone.properties?.rectangle?.styles ?? {};
  const textStyles: Partial<TextProperties["styles"]> =
    newZone.properties?.text?.styles ?? {};
  const translations: Record<Lang, string> =
    newZone.properties?.text?.translations ?? defaultZoneTranslations;

  return (
    <>
      <Divider mt={15} mb={10} />
      <Group px={px}>
        <Title order={4}>
          {isEditMode
            ? t("scheme.redactor.panelOptions.setFanZone.editTitle")
            : t("scheme.redactor.panelOptions.setFanZone.addTitle")}
        </Title>

        {(["en", "ro", "ru"] as Lang[]).map((lang) => (
          <Flex w={"100%"} direction="column" key={lang}>
            <InputLabel>
              {t(
                `scheme.redactor.panelOptions.setText.translations.${lang}.title`
              )}
            </InputLabel>
            <TextInput
              value={translations[lang] || ""}
              onChange={(e) =>
                updateZone(
                  ["properties", "text", "translations", lang],
                  e.currentTarget.value
                )
              }
              placeholder={t(
                `scheme.redactor.panelOptions.setText.translations.${lang}.placeholder`
              )}
            />
          </Flex>
        ))}

        <Flex gap={10} align={"center"}>
          <NumberInput
            label={t("scheme.redactor.panelOptions.setFanZone.maxSeats")}
            min={1}
            value={newZone.max_seats}
            onChange={(v) =>
              updateZone(["max_seats"], typeof v === "number" ? v : 1)
            }
          />

          <Checkbox
            mt="sm"
            label={t("scheme.redactor.panelOptions.setFanZone.fixedSeats")}
            checked={newZone.fixed_seats}
            onChange={(e) =>
              updateZone(["fixed_seats"], e.currentTarget.checked)
            }
          />
        </Flex>
      </Group>

      <Divider my="md" variant="dashed" />

      <Group px={px}>
        <Title order={6}>
          {t("scheme.redactor.panelOptions.setFanZone.rectangleStyles")}
        </Title>

        <ColorInput
          label={t(
            "scheme.redactor.panelOptions.setFigure.polilyne.strokeColor"
          )}
          value={stylesRectangles.fill || "#f0f0f0"}
          onChange={(val) =>
            updateZone(["properties", "rectangle", "styles", "fill"], val)
          }
        />

        <Flex gap="sm">
          <NumberInput
            label={t("scheme.redactor.panelOptions.setFigure.rect.width")}
            value={parseFloat(stylesRectangles.width ?? "100")}
            onChange={(val) =>
              updateZone(
                ["properties", "rectangle", "styles", "width"],
                String(val ?? 100)
              )
            }
          />
          <NumberInput
            label={t("scheme.redactor.panelOptions.setFigure.rect.height")}
            value={parseFloat(stylesRectangles.height ?? "50")}
            onChange={(val) =>
              updateZone(
                ["properties", "rectangle", "styles", "height"],
                String(val ?? 50)
              )
            }
          />
        </Flex>

        <Title order={6}>
          {t("scheme.redactor.panelOptions.setFanZone.textStyles")}
        </Title>

        <Flex gap="sm">
          <ColorInput
            label={t(
              "scheme.redactor.panelOptions.setFigure.polilyne.strokeColor"
            )}
            value={textStyles.fill || "#000"}
            onChange={(val) =>
              updateZone(["properties", "text", "styles", "fill"], val)
            }
          />
          <NumberInput
            label={t("scheme.redactor.panelOptions.setText.fontSize")}
            value={textStyles.fontSize || 12}
            onChange={(val) =>
              updateZone(
                ["properties", "text", "styles", "fontSize"],
                val || 12
              )
            }
          />
        </Flex>

        <Button mt="md" onClick={handleSave}>
          {isEditMode
            ? t("scheme.redactor.panelOptions.setFanZone.actions.update")
            : t("scheme.redactor.panelOptions.setFanZone.actions.insert")}
        </Button>
      </Group>
    </>
  );
};

export default React.memo(FanZone);
